import Card from "components/card";
import React, { useState } from "react";
import { IoAdd, IoClose, IoCheckmark, IoTrash, IoPencil } from "react-icons/io5";
import { addLeadNote, deleteLeadNote, updateLeadNote } from "../../tables/leadApi";
import { usePermission } from "hooks/usePermission";

function Note({ lead = {}, onNotesUpdate }) {
  const [notes, setNotes] = useState(lead?.notes || []);
  const [showForm, setShowForm] = useState(false);
  const [formValue, setFormValue] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editFormValue, setEditFormValue] = useState("");
  const [editCharCount, setEditCharCount] = useState(0);
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const { can } = usePermission();
  const canCreateLead = can("lead_management", "create");
  const canUpdateLead = can("lead_management", "update");
  const canDeleteLead = can("lead_management", "delete");
  const leadId = lead?._id || lead?.id;

  const handleAddClick = () => {
    setFormValue("");
    setCharCount(0);
    setError("");
    setShowForm(true);
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    if (value.length <= 1000) {
      setFormValue(value);
      setCharCount(value.length);
    }
  };

  const handleSave = async () => {
    const trimmed = formValue.trim();
    if (!trimmed) {
      setError("Please enter a note.");
      return;
    }
    if (!leadId) {
      setError("Unable to identify lead.");
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      const responseData = await addLeadNote({ leadId, note: trimmed });
      const notesFromApi = responseData?.data?.notes;
      const normalizedNotes = Array.isArray(notesFromApi)
        ? notesFromApi
            .filter((item) => item && (item.note || item.content))
            .map((item) => ({
              _id: item._id || item.id,
              note: item.note || item.content,
              date: item.date || item.createdAt || new Date().toISOString(),
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date))
        : [{ _id: `note-${Date.now()}`, note: trimmed, date: new Date().toISOString() }];

      setNotes(normalizedNotes);
      setShowForm(false);
      setFormValue("");
      setCharCount(0);

      if (onNotesUpdate) {
        onNotesUpdate(normalizedNotes);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to add note.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;

    const noteId = noteToDelete?._id || noteToDelete?.id;
    if (!noteId) return;
    if (!leadId) {
      setError("Unable to identify lead.");
      setNoteToDelete(null);
      return;
    }

    setIsDeleting(true);
    setError("");
    try {
      await deleteLeadNote(leadId, noteId);
      const updatedNotes = notes.filter((n) => (n._id || n.id) !== noteId);
      setNotes(updatedNotes);
      setNoteToDelete(null);
      if (onNotesUpdate) {
        onNotesUpdate(updatedNotes);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete note.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setNoteToDelete(null);
    setError("");
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormValue("");
    setCharCount(0);
    setError("");
  };

  const handleEditClick = (note) => {
    setEditingNoteId(note._id || note.id);
    setEditFormValue(note.note || note.content);
    setEditCharCount((note.note || note.content).length);
    setError("");
  };

  const handleEditTextChange = (e) => {
    const value = e.target.value;
    if (value.length <= 1000) {
      setEditFormValue(value);
      setEditCharCount(value.length);
    }
  };

  const handleEditSave = async () => {
    const trimmed = editFormValue.trim();
    if (!trimmed) {
      setError("Please enter a note.");
      return;
    }
    if (!leadId) {
      setError("Unable to identify lead.");
      return;
    }

    setIsEditSaving(true);
    setError("");
    try {
      await updateLeadNote(leadId, editingNoteId, { note: trimmed });
      const updatedNotes = notes.map((n) =>
        (n._id || n.id) === editingNoteId
          ? { ...n, note: trimmed, date: n.date }
          : n
      );
      setNotes(updatedNotes);
      setEditingNoteId(null);
      setEditFormValue("");
      setEditCharCount(0);

      if (onNotesUpdate) {
        onNotesUpdate(updatedNotes);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update note.");
    } finally {
      setIsEditSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingNoteId(null);
    setEditFormValue("");
    setEditCharCount(0);
    setError("");
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Card extra={"w-full h-full p-4 flex flex-col gap-3 overflow-hidden"}>
      {/* Header */}
      <div className="relative mb-2 flex items-center justify-between">
        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
          Notes
        </h4>
        <div className="flex items-center gap-2">
          {!showForm && canCreateLead && (
            <button
              onClick={handleAddClick}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-3 py-1.5 text-white hover:bg-brand-600 transition-colors"
              title="Add Note"
            >
              <IoAdd className="text-lg" />
            </button>
          )}
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-700/50">
          <div className="mb-3 flex items-center justify-between">
            <h5 className="text-sm font-semibold text-navy-700 dark:text-white">
              New Note
            </h5>
            <button
              onClick={handleCancel}
              className="rounded-full p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <IoClose className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-200">
              {error}
            </div>
          )}

          <textarea
            value={formValue}
            onChange={handleTextChange}
            placeholder="Write your note here..."
            className="mb-3 min-h-[100px] w-full resize-none rounded-lg border border-gray-200 bg-white p-3 text-sm text-navy-700 placeholder-gray-400 outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:placeholder-gray-500"
            maxLength="1000"
          />

          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {charCount}/1000 characters
            </span>
            {charCount > 900 && (
              <span className="text-xs text-red-500">
                Approaching character limit
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`flex items-center gap-1 flex-1 justify-center rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors ${isSaving ? "bg-green-300 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"}`}
            >
              <IoCheckmark className="h-4 w-4" />
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center gap-1 flex-1 justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Notes List with Scroll */}
      <div className="max-h-96 overflow-y-auto flex-1 min-h-0 pr-1">
        {notes.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 py-8 dark:border-navy-600">
            <p className="text-sm text-gray-400 dark:text-gray-500">
              No notes yet. Add one to get started!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div
                key={note._id || note.id}
                className="rounded-lg border border-gray-200 bg-white p-3 transition-all hover:shadow-md dark:border-navy-600 dark:bg-navy-800"
              >
                {editingNoteId === (note._id || note.id) ? (
                  // Edit Mode
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <h5 className="text-sm font-semibold text-navy-700 dark:text-white">
                        Edit Note
                      </h5>
                      <button
                        onClick={handleEditCancel}
                        className="rounded-full p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <IoClose className="h-5 w-5" />
                      </button>
                    </div>

                    {error && (
                      <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-200">
                        {error}
                      </div>
                    )}

                    <textarea
                      value={editFormValue}
                      onChange={handleEditTextChange}
                      placeholder="Edit your note..."
                      className="mb-3 min-h-[100px] w-full resize-none rounded-lg border border-gray-200 bg-white p-3 text-sm text-navy-700 placeholder-gray-400 outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:placeholder-gray-500"
                      maxLength="1000"
                    />

                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {editCharCount}/1000 characters
                      </span>
                      {editCharCount > 900 && (
                        <span className="text-xs text-red-500">
                          Approaching character limit
                        </span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={handleEditSave}
                        disabled={isEditSaving}
                        className={`flex items-center gap-1 flex-1 justify-center rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors ${isEditSaving ? "bg-green-300 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"}`}
                      >
                        <IoCheckmark className="h-4 w-4" />
                        {isEditSaving ? "Saving..." : "Save"}
                      </button>
                      <button
                        onClick={handleEditCancel}
                        className="flex items-center gap-1 flex-1 justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <>
                    <div className="mb-2 flex items-start justify-between">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {formatDate(note.date)}
                      </p>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditClick(note)}
                          className="rounded-full p-1.5 text-gray-400 hover:text-blue-500 transition-colors dark:hover:text-blue-400"
                          title="Edit"
                        >
                          <IoPencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(note)}
                          className="rounded-full p-1.5 text-gray-400 hover:text-red-500 transition-colors dark:hover:text-red-400"
                          title="Delete"
                        >
                          <IoTrash className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed break-words dark:text-gray-300">
                      {note.note || note.content}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {noteToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 dark:bg-navy-800">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                <IoTrash className="text-xl text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-navy-700 dark:text-white">
                Delete Note
              </h2>
            </div>

            <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to delete this note? This action cannot be undone.
            </p>

            {error && (
              <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-200">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
                  isDeleting
                    ? "bg-red-300 cursor-not-allowed"
                    : "bg-red-500 hover:bg-red-600"
                }`}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={handleDeleteCancel}
                disabled={isDeleting}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default Note;
